import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import StoreMallDirectoryOutlinedIcon from '@mui/icons-material/StoreMallDirectoryOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { ReactNode } from 'react';

export type Area = 'learner' | 'provider' | 'admin';

export interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  /** Match child routes too (default true) */
  prefix?: boolean;
  requiresAuth?: boolean;
}

export const learnerNav: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <DashboardOutlinedIcon />, requiresAuth: true },
  { label: 'My Question Banks', to: '/question-banks', icon: <LibraryBooksOutlinedIcon />, requiresAuth: true },
  { label: 'Practice', to: '/practice', icon: <PlayCircleOutlineIcon />, requiresAuth: true },
  { label: 'Exams', to: '/exam', icon: <TimerOutlinedIcon />, requiresAuth: true },
  { label: 'Progress', to: '/progress', icon: <TrendingUpIcon />, requiresAuth: true },
  { label: 'Study plan', to: '/study-plan', icon: <EventOutlinedIcon />, requiresAuth: true },
  { label: 'Store', to: '/store', icon: <StorefrontOutlinedIcon /> },
  { label: 'Purchases', to: '/purchases', icon: <ShoppingBagOutlinedIcon />, requiresAuth: true },
  { label: 'TradePrep Plus', to: '/plus', icon: <AutoAwesomeOutlinedIcon />, requiresAuth: true },
  { label: 'Profile', to: '/profile', icon: <AccountCircleOutlinedIcon />, requiresAuth: true },
  { label: 'Help & support', to: '/support', icon: <HelpOutlineIcon /> },
];

/** Phone bottom navigation: the five things learners do most. */
export const learnerBottomNav: NavItem[] = [
  { label: 'Home', to: '/dashboard', icon: <DashboardOutlinedIcon /> },
  { label: 'Banks', to: '/question-banks', icon: <LibraryBooksOutlinedIcon /> },
  { label: 'Practice', to: '/practice', icon: <PlayCircleOutlineIcon /> },
  { label: 'Exam', to: '/exam', icon: <TimerOutlinedIcon /> },
  { label: 'Store', to: '/store', icon: <StorefrontOutlinedIcon /> },
];

export const providerNav: NavItem[] = [
  { label: 'Provider Dashboard', to: '/provider', icon: <DashboardOutlinedIcon />, prefix: false },
  { label: 'My Products', to: '/provider/products', icon: <Inventory2OutlinedIcon /> },
  { label: 'Question Banks', to: '/provider/question-banks', icon: <LibraryBooksOutlinedIcon /> },
  { label: 'Sales', to: '/provider/sales', icon: <ReceiptLongOutlinedIcon /> },
  { label: 'Analytics', to: '/provider/analytics', icon: <InsightsOutlinedIcon /> },
  { label: 'Payouts', to: '/provider/payouts', icon: <PaymentsOutlinedIcon /> },
  { label: 'Workshop', to: '/provider/workshop', icon: <HandymanOutlinedIcon /> },
  { label: 'Provider Profile', to: '/provider/profile', icon: <StoreMallDirectoryOutlinedIcon /> },
  { label: 'Credentials', to: '/provider/credentials', icon: <VerifiedOutlinedIcon /> },
  { label: 'Help & support', to: '/support', icon: <HelpOutlineIcon /> },
];

export const adminNav: NavItem[] = [
  { label: 'Overview', to: '/admin', icon: <BarChartOutlinedIcon />, prefix: false },
  { label: 'Engagement', to: '/admin/engagement', icon: <InsightsOutlinedIcon /> },
  { label: 'Users', to: '/admin/users', icon: <GroupOutlinedIcon /> },
  { label: 'Content Providers', to: '/admin/providers', icon: <StoreMallDirectoryOutlinedIcon /> },
  { label: 'Credentials', to: '/admin/credentials', icon: <VerifiedOutlinedIcon /> },
  { label: 'Products', to: '/admin/products', icon: <FactCheckOutlinedIcon /> },
  { label: 'Question Banks', to: '/admin/question-banks', icon: <LibraryBooksOutlinedIcon /> },
  { label: 'Orders & Payments', to: '/admin/orders', icon: <ReceiptLongOutlinedIcon /> },
  { label: 'Payouts', to: '/admin/payouts', icon: <PaymentsOutlinedIcon /> },
  { label: 'Free access', to: '/admin/gifts', icon: <CardGiftcardOutlinedIcon /> },
  { label: 'Reviews', to: '/admin/reviews', icon: <RateReviewOutlinedIcon /> },
  { label: 'Catalogue', to: '/admin/catalogue', icon: <CategoryOutlinedIcon /> },
  { label: 'AI & Plus', to: '/admin/ai', icon: <AutoAwesomeOutlinedIcon /> },
  { label: 'Platform Settings', to: '/admin/settings', icon: <SettingsOutlinedIcon /> },
];

export const areaMeta: Record<Area, { label: string; home: string; icon: ReactNode }> = {
  learner: { label: 'Learn', home: '/dashboard', icon: <AssignmentOutlinedIcon fontSize="small" /> },
  provider: { label: 'Provider', home: '/provider', icon: <StoreMallDirectoryOutlinedIcon fontSize="small" /> },
  admin: { label: 'Admin', home: '/admin', icon: <AdminPanelSettingsOutlinedIcon fontSize="small" /> },
};

export function areaFromPath(pathname: string): Area {
  if (pathname.startsWith('/provider')) return 'provider';
  if (pathname.startsWith('/admin')) return 'admin';
  return 'learner';
}

export function isActive(item: NavItem, pathname: string) {
  if (item.prefix === false) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}
